import PublicLayout from "@/components/layout/PublicLayout";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { uploadPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Home as HomeIcon, Building2, UploadCloud, ShieldCheck, FileText, MapPin, Compass, Calendar, ArrowRight, ChevronLeft } from "lucide-react";
import { track } from "@/lib/analytics";
import { pickVariant, exposeVariant, recordConversion } from "@/lib/experiments";

const TRY_SAMPLE_EXPERIMENT = "try_sample_cta_v1";
const TRY_SAMPLE_VARIANTS = ["control", "real_report"];
const TRY_SAMPLE_COPY = {
    control: "Try a sample preview",
    real_report: "See it on a real report",
};

const MAX_MB = 25;
const SURVEY_KEY = "bl_deal_stage_survey_v1";

function loadSurvey() {
    try {
        const raw = localStorage.getItem(SURVEY_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveSurvey(s) {
    try {
        localStorage.setItem(SURVEY_KEY, JSON.stringify(s));
    } catch { /* non-critical */ }
}

const DEAL_STAGES = [
    { id: "exploring", label: "Just exploring", desc: "Researching homes, not under contract." },
    { id: "touring", label: "Touring / making offers", desc: "Looking at properties or in early negotiation." },
    { id: "under_contract", label: "Under contract", desc: "Offer accepted, inspection coming up or done." },
    { id: "inspection_done", label: "Inspection in hand", desc: "Report received — preparing to negotiate or close." },
];

const URGENCIES = [
    { id: "this_week", label: "This week" },
    { id: "this_month", label: "This month" },
    { id: "exploring", label: "No rush" },
];

function DealStageStep({ persona, value, onSubmit, onSkip, onBack }) {
    const [dealStage, setDealStage] = useState(value?.dealStage || "");
    const [urgency, setUrgency] = useState(value?.urgency || "");
    return (
        <div data-testid="deal-stage-step">
            <button
                type="button"
                onClick={onBack}
                className="bl-btn-ghost text-xs mb-4"
                data-testid="deal-stage-back"
            >
                <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <div className="bl-eyebrow">Step 2 of 3 · Optional</div>
            <h2 className="mt-4 font-display font-semibold text-3xl md:text-5xl tracking-tight text-bl max-w-2xl leading-[1.05]">
                Where are you in{" "}
                <span className="font-italic-accent text-bl-primary">the deal</span>?
            </h2>
            <p className="mt-4 text-bl-muted max-w-xl leading-relaxed">
                One quick question lets us match the report tone and sequencing
                to your moment — free, no spam, skip anytime.
            </p>

            <div className="mt-8 space-y-3">
                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                    <Compass className="w-3 h-3 inline mr-1" /> Deal stage
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {DEAL_STAGES.map((s, i) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setDealStage(s.id)}
                            className={
                                "bl-card p-4 text-left transition-all border " +
                                (dealStage === s.id
                                    ? "bl-card-selected"
                                    : "border-bl hover:border-bl-primary/50")
                            }
                            data-testid={`deal-stage-${s.id}`}
                            data-reveal="rise-sm"
                            data-reveal-delay={Math.min(5, i + 1)}
                        >
                            <div className="font-display font-semibold text-bl text-sm">
                                {s.label}
                            </div>
                            <div className="mt-1 text-xs text-bl-muted leading-relaxed">
                                {s.desc}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6 space-y-3">
                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                    <Calendar className="w-3 h-3 inline mr-1" /> Urgency
                </div>
                <div className="flex flex-wrap gap-2">
                    {URGENCIES.map((u) => (
                        <button
                            key={u.id}
                            type="button"
                            onClick={() => setUrgency(u.id)}
                            className={
                                "px-3 py-1.5 rounded-full text-xs font-medium transition border " +
                                (urgency === u.id
                                    ? "bg-bl-primary text-white border-bl-primary"
                                    : "bg-bl-surface text-bl-muted border-bl hover:text-bl")
                            }
                            data-testid={`urgency-${u.id}`}
                        >
                            {u.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={() => {
                        track("deal_stage_submitted", { persona, deal_stage: dealStage || null, urgency: urgency || null });
                        onSubmit({ dealStage, urgency });
                    }}
                    disabled={!dealStage && !urgency}
                    className="bl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="deal-stage-continue"
                >
                    Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        track("deal_stage_skipped", { persona });
                        onSkip();
                    }}
                    className="bl-btn-ghost"
                    data-testid="deal-stage-skip"
                >
                    Skip for now
                </button>
            </div>
        </div>
    );
}

function PathStep({ persona, setPersona }) {
    return (
        <div data-testid="path-step">
            <div className="bl-eyebrow">Step 1 of 3</div>
            <h2 className="mt-4 font-display font-semibold text-3xl md:text-5xl tracking-tight text-bl max-w-2xl leading-[1.05]">
                Tell us a bit about{" "}
                <span className="font-italic-accent text-bl-primary">why</span>{" "}
                you're here.
            </h2>
            <p className="mt-4 text-bl-muted max-w-xl leading-relaxed">
                This personalizes your dashboard, recommendations, and analysis tone.
            </p>
            <div className="mt-8 grid md:grid-cols-2 gap-5">
                {[
                    {
                        id: "buyer",
                        title: "Buying a home",
                        desc: "I'm trying to understand my inspection and negotiate more confidently.",
                        icon: HomeIcon,
                    },
                    {
                        id: "investor",
                        title: "Investing in property",
                        desc: "I'm analyzing repair exposure, risk, and deal quality.",
                        icon: Building2,
                    },
                ].map(({ id, title, desc, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setPersona(id)}
                        className={
                            "bl-card bl-card-hover p-7 text-left transition-all " +
                            (persona === id
                                ? "ring-2 ring-bl-primary border-bl-primary"
                                : "")
                        }
                        data-testid={`persona-${id}-card`}
                    >
                        <div className="w-10 h-10 rounded-lg bg-bl-teal-tint flex items-center justify-center text-bl-primary">
                            <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="mt-5 font-display font-semibold text-xl text-bl">
                            {title}
                        </h3>
                        <p className="mt-2 text-bl-muted leading-relaxed">{desc}</p>
                    </button>
                ))}
            </div>
        </div>
    );
}

function UploadStep({ persona, survey, onUploaded }) {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const tryVariant = pickVariant(TRY_SAMPLE_EXPERIMENT, TRY_SAMPLE_VARIANTS);
    const tryCopy = TRY_SAMPLE_COPY[tryVariant] || TRY_SAMPLE_COPY.control;
    useEffect(() => {
        exposeVariant(TRY_SAMPLE_EXPERIMENT, tryVariant);
        try { track("upload_try_sample_impression", { variant: tryVariant }); } catch { /* non-critical */ }
    }, [tryVariant]);
    const [drag, setDrag] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState("");
    const [file, setFile] = useState(null);
    const [zip, setZip] = useState("");
    const [marketHint, setMarketHint] = useState(null);

    // Lazy market lookup on ZIP change (debounced)
    useEffect(() => {
        const z = zip.trim();
        if (z.length !== 5 || !/^\d{5}$/.test(z)) {
            setMarketHint(null);
            return;
        }
        let active = true;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/cost-engine/market-lookup?zip_code=${z}`,
                );
                const d = await r.json();
                if (active) setMarketHint(d);
            } catch {
                if (active) setMarketHint(null);
            }
        }, 350);
        return () => {
            active = false;
            clearTimeout(t);
        };
    }, [zip]);

    const onPick = (f) => {
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Only PDF files are supported.");
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            toast.error(`File too large. Max ${MAX_MB}MB.`);
            return;
        }
        setFile(f);
    };

    const startUpload = async () => {
        if (!file) return;
        setUploading(true);
        setStatus("Uploading your PDF…");
        track("upload_started", {
            persona,
            size_bytes: file.size,
            zip: zip || null,
            deal_stage: survey?.dealStage || null,
            urgency: survey?.urgency || null,
        });
        try {
            const res = await uploadPdf(file, persona, zip || undefined, {
                dealStage: survey?.dealStage || undefined,
                urgency: survey?.urgency || undefined,
            });
            setStatus("Reading the report…");
            if (res?.upload?.status === "failed") {
                track("upload_failed", {
                    persona,
                    reason: res.upload.extraction_status || "unknown",
                });
                toast.error(
                    res.upload.extraction_error ||
                        "We couldn't read this PDF. Please try a different file.",
                );
                setUploading(false);
                return;
            }
            // brief reassuring delay
            await new Promise((r) => setTimeout(r, 600));
            setStatus("Organizing findings…");
            await new Promise((r) => setTimeout(r, 600));
            track("upload_completed", {
                persona,
                upload_id: res?.upload?.upload_id,
                page_count: res?.upload?.page_count,
                report_id: res?.report_id,
            });
            onUploaded(res);
        } catch (e) {
            track("upload_failed", { persona, reason: "network" });
            toast.error("Upload failed. Please try again.");
            setUploading(false);
        }
    };

    return (
        <div data-testid="upload-step">
            <div className="bl-eyebrow">Step 3 of 3</div>
            <h2 className="mt-4 font-display font-semibold text-3xl md:text-5xl tracking-tight text-bl max-w-2xl leading-[1.05]">
                Upload your inspection{" "}
                <span className="font-italic-accent text-bl-primary">PDF</span>.
            </h2>
            <p className="mt-4 text-bl-muted max-w-xl leading-relaxed">
                Drag, drop, done. Text-based PDFs only, up to {MAX_MB}MB.
            </p>

            <div className="mt-4 flex items-center gap-3 flex-wrap text-sm" data-testid="upload-try-sample-row">
                <span className="font-mono-bl text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                    Don't have a report yet?
                </span>
                <button
                    type="button"
                    onClick={() => {
                        try {
                            track("upload_try_sample_click", { variant: tryVariant });
                            recordConversion(
                                TRY_SAMPLE_EXPERIMENT, tryVariant,
                                "upload_try_sample_click",
                            );
                        } catch { /* non-critical */ }
                        navigate("/sample-analysis");
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-bl-primary text-bl-primary hover:bg-bl-teal-tint font-medium transition-colors"
                    data-testid="upload-try-sample-button"
                    data-variant={tryVariant}
                >
                    {tryCopy}
                    <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>

            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    onPick(e.dataTransfer.files?.[0]);
                }}
                className={
                    "mt-8 bl-card border-dashed border-2 transition-all flex flex-col items-center justify-center text-center p-12 md:p-16 cursor-pointer " +
                    (drag
                        ? "border-bl-primary bg-bl-teal-tint"
                        : "border-bl hover:bg-bl-surface")
                }
                onClick={() => inputRef.current?.click()}
                data-testid="upload-dropzone"
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => onPick(e.target.files?.[0])}
                    data-testid="upload-file-input"
                />
                {!file ? (
                    <>
                        <div className="w-14 h-14 rounded-full bg-bl-teal-tint flex items-center justify-center text-bl-primary">
                            <UploadCloud className="w-6 h-6" />
                        </div>
                        <h3 className="mt-5 font-display font-semibold text-xl text-bl">
                            Drop your inspection PDF here
                        </h3>
                        <p className="mt-2 text-bl-muted">or click to browse</p>
                        <div className="mt-5 font-mono-bl text-xs text-bl-muted">
                            Max {MAX_MB}MB · PDF only
                        </div>
                    </>
                ) : (
                    <>
                        <div className="w-14 h-14 rounded-full bg-bl-teal-tint flex items-center justify-center text-bl-primary">
                            <FileText className="w-6 h-6" />
                        </div>
                        <h3 className="mt-5 font-display font-semibold text-xl text-bl">
                            {file.name}
                        </h3>
                        <p className="mt-2 font-mono-bl text-sm text-bl-muted">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                    </>
                )}
            </div>

            {file && !uploading && (
                <>
                    <div
                        className="mt-6 bl-card p-5"
                        data-testid="zip-input-section"
                    >
                        <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5" />
                            Property ZIP (optional — used to localize repair costs)
                        </label>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <input
                                value={zip}
                                onChange={(e) =>
                                    setZip(
                                        e.target.value.replace(/[^0-9]/g, "").slice(0, 5),
                                    )
                                }
                                placeholder="e.g. 28801"
                                inputMode="numeric"
                                maxLength={5}
                                className="px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm font-mono-bl tnum w-32 outline-none focus:border-bl-primary"
                                data-testid="zip-input"
                            />
                            {marketHint?.known ? (
                                <span
                                    className="bl-badge bl-badge-neutral"
                                    data-testid="market-hint-pill"
                                    title={`Labor ${marketHint.profile.labor_mult}× · Material ${marketHint.profile.material_mult}×`}
                                >
                                    {marketHint.profile.name} ·{" "}
                                    {marketHint.profile.labor_mult.toFixed(2)}× labor
                                </span>
                            ) : zip.length === 5 ? (
                                <span
                                    className="bl-badge bl-badge-medium"
                                    data-testid="market-hint-fallback"
                                >
                                    No local profile — using national average
                                </span>
                            ) : (
                                <span className="text-xs text-bl-muted">
                                    Leave blank to estimate at a national average.
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="mt-5 flex gap-3">
                        <button
                            className="bl-btn-primary"
                            onClick={startUpload}
                            data-testid="upload-confirm-btn"
                        >
                            Analyze this report
                        </button>
                        <button
                            className="bl-btn-secondary"
                            onClick={() => setFile(null)}
                            data-testid="upload-clear-btn"
                        >
                            Choose another
                        </button>
                    </div>
                </>
            )}

            {uploading && (
                <div
                    className="mt-6 bl-card p-6"
                    data-testid="upload-processing"
                >
                    <div className="flex items-center gap-4">
                        <div className="bl-spinner" />
                        <div className="min-w-0 flex-1">
                            <div className="font-display font-medium text-bl">
                                {status}
                            </div>
                            <p className="text-sm text-bl-muted mt-1 leading-relaxed">
                                You can safely leave this page — we'll save
                                your report to your dashboard when it's ready.
                                Large scanned PDFs may take a little longer.
                            </p>
                        </div>
                    </div>
                    {/* Processing stages — calm, deterministic visual */}
                    <ol className="mt-5 space-y-1.5 text-xs text-bl-muted">
                        {[
                            "Upload received",
                            "Extracting inspection findings",
                            "Organizing report sections",
                            "Estimating professional repair exposure",
                            "Preparing your report",
                        ].map((stage, i) => (
                            <li
                                key={stage}
                                className="flex items-center gap-2 leading-tight"
                            >
                                <span className="font-mono-bl tnum text-bl-primary">
                                    0{i + 1}
                                </span>
                                {stage}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            <div className="mt-8 flex items-start gap-3 text-sm text-bl-muted max-w-xl">
                <ShieldCheck className="w-4 h-4 text-bl-primary mt-0.5 flex-shrink-0" />
                <span>
                    Your file is stored securely and is never publicly indexable.
                    Reports are private to your account.
                </span>
            </div>
        </div>
    );
}

export default function Upload() {
    const [persona, setPersona] = useState(null);
    const [survey, setSurvey] = useState(null);  // {dealStage, urgency} | null
    const [surveyDone, setSurveyDone] = useState(false);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // If the user is already known to be an investor (persona on profile, OR an
    // active investor subscription), skip the "tell us why you're here" step.
    useEffect(() => {
        if (!persona && user) {
            if (user.active_subscription || user.persona === "investor") {
                setPersona("investor");
            } else if (user.persona === "buyer") {
                setPersona("buyer");
            }
        }
    }, [user, persona]);

    // Load any saved survey so repeat users skip the question.
    useEffect(() => {
        const saved = loadSurvey();
        if (saved) {
            setSurvey(saved);
            setSurveyDone(true);
        }
    }, []);

    // After upload we either go to the soft-account gate or straight to the report
    const onUploaded = (res) => {
        const uploadId = res?.upload?.upload_id;
        const reportId = res?.report_id;
        const intent = params.get("intent");
        if (!uploadId) return;

        if (!user) {
            const qs = new URLSearchParams({
                next: reportId ? `/report/${reportId}` : `/dashboard`,
                upload_id: uploadId,
            });
            if (intent) qs.set("intent", intent);
            navigate(`/auth/gate?${qs.toString()}`);
        } else {
            navigate(reportId ? `/report/${reportId}` : `/dashboard`);
        }
    };

    const handleSurveySubmit = (s) => {
        setSurvey(s);
        setSurveyDone(true);
        saveSurvey(s);
    };

    const handleSurveySkip = () => {
        setSurveyDone(true);
        // We intentionally do NOT save an empty survey so the question
        // can appear again later if the user comes back.
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container py-16" data-testid="upload-page">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7 min-w-0">
                            {!persona ? (
                                <PathStep persona={persona} setPersona={setPersona} />
                            ) : !surveyDone ? (
                                <DealStageStep
                                    persona={persona}
                                    value={survey}
                                    onSubmit={handleSurveySubmit}
                                    onSkip={handleSurveySkip}
                                    onBack={() => setPersona(null)}
                                />
                            ) : (
                                <UploadStep persona={persona} survey={survey} onUploaded={onUploaded} />
                            )}
                        </div>

                        {/* Contextual side card — what's next + privacy assurance */}
                        <aside className="lg:col-span-5 min-w-0" data-testid="upload-side-card">
                            <div className="bl-card p-6 md:p-7 lg:sticky lg:top-24">
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        <Compass className="w-3.5 h-3.5" />
                                        How this works
                                    </div>
                                    <span className="font-mono-bl text-xs text-bl-muted">
                                        ~3 min total
                                    </span>
                                </div>

                                <ol className="mt-5 space-y-3">
                                    {[
                                        ["Tell us why you're here", "Helps us tune analysis tone (buyer vs investor)."],
                                        ["Tell us where you are in the deal", "Optional — adjusts what we surface first."],
                                        ["Upload your inspection PDF", "We organize the findings and you choose your tier."],
                                    ].map(([title, desc], i) => {
                                        const stepNum = i + 1;
                                        const isActive =
                                            (!persona && stepNum === 1) ||
                                            (persona && !surveyDone && stepNum === 2) ||
                                            (persona && surveyDone && stepNum === 3);
                                        const isDone =
                                            (persona && stepNum < 2) ||
                                            (surveyDone && stepNum < 3);
                                        return (
                                            <li
                                                key={title}
                                                className={
                                                    "flex items-start gap-3 rounded-lg px-3 py-2.5 border " +
                                                    (isActive
                                                        ? "bg-bl-primary/5 border-bl-primary/30"
                                                        : isDone
                                                          ? "bg-bl-cream border-bl"
                                                          : "border-bl")
                                                }
                                            >
                                                <span
                                                    className={
                                                        "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono-bl shrink-0 " +
                                                        (isActive
                                                            ? "bg-bl-primary text-white"
                                                            : isDone
                                                              ? "bg-bl text-white"
                                                              : "bg-bl-cream text-bl-muted")
                                                    }
                                                >
                                                    {isDone ? "✓" : stepNum}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-bl">
                                                        {title}
                                                    </div>
                                                    <div className="text-xs text-bl-muted mt-0.5 leading-relaxed">
                                                        {desc}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>

                                <div className="mt-5 pt-5 border-t border-bl">
                                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                        Privacy first
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        {[
                                            [ShieldCheck, "Private upload, tied to your account"],
                                            [FileText, "Stored encrypted; deletable anytime"],
                                        ].map(([Ico, label]) => (
                                            <div
                                                key={label}
                                                className="flex items-start gap-2.5 text-xs text-bl"
                                            >
                                                <Ico className="w-3.5 h-3.5 text-bl-primary mt-0.5 shrink-0" />
                                                <span>{label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
